import React from 'react';
import { useParams } from 'react-router-dom';
import { Paper, Box, Typography, Button, CircularProgress, Alert } from '@mui/material';
import { FileDownloadOutlined, CheckRounded, ScheduleRounded } from '@mui/icons-material';
import { Crumbs, DetailHead, TabBar, KV, StatBox, SectionCard, Grid, useTab } from '../../components/common';
import DataTable, { Column } from '../../components/DataTable';
import StatusChip from '../../components/StatusChip';
import { useToast } from '../../components/Toast';
import { usePaymentDetail } from '../../hooks/usePayments';
import { money, statusLabel } from '../../utils/format';
import { colors } from '../../theme/theme';

const PaymentDetailPage: React.FC = () => {
  const { id } = useParams();
  const toast = useToast();
  const { data: p, loading, error, reload } = usePaymentDetail(id);
  const [tab, setTab] = useTab('overview');

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', minHeight: 300, alignItems: 'center' }}><CircularProgress /></Box>;
  }
  if (error || !p) {
    return <Alert severity="error" action={<Button color="inherit" size="small" onClick={reload}>Retry</Button>}>{error || 'Not found.'}</Alert>;
  }

  const amount = Number(p.amount || 0);
  const subtotal = p.subtotal != null ? Number(p.subtotal) : Math.round(amount / 1.18);
  const gst = p.tax_amount != null ? Number(p.tax_amount) : amount - subtotal;
  const paid = p.status === 'paid';
  const download = () => toast(p.invoice && p.invoice !== '—' ? `Invoice ${p.invoice} downloaded` : 'No invoice available for this payment', p.invoice && p.invoice !== '—' ? 'success' : 'info');

  const lineCols: Column<{ d: string; qty: number; amt: number }>[] = [
    { key: 'd', label: 'Description' },
    { key: 'qty', label: 'Qty', align: 'right' },
    { key: 'amt', label: 'Amount', align: 'right', render: (r) => money(r.amt) },
  ];

  const timeline: Array<[string, string, boolean]> = [
    ['Order created', p.date, true],
    ['Payment authorized', p.date, paid || p.status === 'pending'],
    ['Payment captured', paid ? p.date : statusLabel(String(p.status)), paid],
    ['Invoice generated', p.invoice && p.invoice !== '—' ? p.date : '—', paid && p.invoice !== '—'],
  ];

  return (
    <>
      <Crumbs items={[{ label: 'Payments', to: '/payments' }, { label: String(p.id).slice(0, 8) }]} />
      <DetailHead
        title="Payment Details"
        actions={<Button variant="contained" startIcon={<FileDownloadOutlined />} onClick={download}>Download Invoice</Button>}
      />

      <Paper sx={{ p: 0, mb: 2.25, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.4fr 1.3fr 1fr' }, '& > div': { p: 2.75 }, '& > div + div': { borderLeft: { md: `1px solid ${colors.border}` }, borderTop: { xs: `1px solid ${colors.border}`, md: 'none' } } }}>
        <Box>
          <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>Amount {paid ? 'paid' : p.status === 'pending' ? 'due' : statusLabel(String(p.status))}</Typography>
          <Typography sx={{ fontSize: 32, fontWeight: 800, letterSpacing: '-.02em', my: 0.5 }}>{money(amount)}</Typography>
          <StatusChip status={String(p.status)} label={statusLabel(String(p.status))} />
          <Typography sx={{ color: 'text.secondary', fontSize: 12, mt: 1.5, fontFamily: 'ui-monospace, monospace' }}>{String(p.id).slice(0, 8)}</Typography>
        </Box>
        <Box>
          <KV k="Business" v={p.business} />
          <KV k="Plan" v={p.plan} />
          <KV k="Invoice #" v={p.invoice} />
          <KV k="Payment Date" v={p.date} />
          <KV k="Razorpay Payment ID" v={p.razorpay_payment_id} />
          <KV k="Razorpay Subscription ID" v={p.razorpay_subscription_id} />
        </Box>
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: 13, mb: 1 }}>Subscription</Typography>
          <StatBox>
            <KV dashed k="Plan Type" v={statusLabel(String(p.plan_type || '—'))} />
            <KV dashed k="Providers" v={p.provider_count ?? '—'} />
            <KV dashed k="Tokens / mo" v={p.token_count ?? '—'} />
            <KV dashed k="Start Date" v={p.start_date} />
            <KV k="Renewal" v={p.renewal} />
          </StatBox>
        </Box>
      </Paper>

      <TabBar active={tab} onChange={setTab} tabs={[{ key: 'overview', label: 'Overview' }, { key: 'invoice', label: 'Invoice' }, { key: 'timeline', label: 'Timeline' }]} />

      {tab === 'overview' && (
        <Grid cols={2}>
          <SectionCard title="Billed To">
            <KV k="Business" v={p.business} />
            <KV k="Owner" v={p.owner} />
            <KV k="Email" v={p.business_email} />
            <KV k="City" v={p.business_city} />
            <KV k="GSTIN" v={p.gstin || '—'} />
          </SectionCard>
          <SectionCard title="Payment Summary">
            <KV k="Plan" v={p.plan} />
            <KV k="Subtotal" v={money(subtotal)} />
            <KV k="GST" v={money(gst)} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1 }}>
              <Typography sx={{ fontWeight: 700 }}>Total</Typography>
              <Typography sx={{ fontWeight: 700, fontSize: 15 }}>{money(p.total_amount != null ? Number(p.total_amount) : amount)}</Typography>
            </Box>
          </SectionCard>
        </Grid>
      )}

      {tab === 'invoice' && (
        <Paper sx={{ p: 3, maxWidth: 720 }}>
          {p.invoice && p.invoice !== '—' ? (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: `1px solid ${colors.border}`, pb: 2, mb: 2 }}>
                <Box>
                  <Typography sx={{ fontWeight: 800, fontSize: 18 }}>BaseQueue</Typography>
                  <Typography sx={{ color: 'text.secondary', fontSize: 12.5 }}>Tax Invoice</Typography>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography sx={{ fontWeight: 600 }}>{p.invoice}</Typography>
                  <Typography sx={{ color: 'text.secondary', fontSize: 12.5 }}>Date: {p.date}</Typography>
                </Box>
              </Box>
              <Grid cols={2} sx={{ mb: 2 }}>
                <Box>
                  <Typography sx={{ color: 'text.secondary', fontSize: 12, mb: 0.5 }}>BILL TO</Typography>
                  <Typography sx={{ fontWeight: 700 }}>{p.business}</Typography>
                  <Typography sx={{ color: 'text.secondary', fontSize: 12.5 }}>{p.business_city}, India</Typography>
                </Box>
              </Grid>
              <DataTable columns={lineCols} rows={[{ d: `${p.plan} subscription`, qty: 1, amt: subtotal }]} />
              <Box sx={{ mt: 1.75, borderTop: `1px solid ${colors.border}`, pt: 1.5 }}>
                <KV k="Subtotal" v={money(subtotal)} />
                <KV k="GST" v={money(gst)} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1 }}>
                  <Typography sx={{ fontWeight: 700 }}>Total {paid ? 'Paid' : 'Due'}</Typography>
                  <Typography sx={{ fontWeight: 700, fontSize: 16 }}>{money(p.total_amount != null ? Number(p.total_amount) : amount)}</Typography>
                </Box>
              </Box>
              <Button variant="contained" startIcon={<FileDownloadOutlined />} onClick={download} sx={{ mt: 2 }}>Download Invoice</Button>
            </>
          ) : (
            <Typography sx={{ color: 'text.secondary', fontSize: 13.5, textAlign: 'center', py: 3 }}>No invoice generated for this payment.</Typography>
          )}
        </Paper>
      )}

      {tab === 'timeline' && (
        <SectionCard>
          {timeline.map(([t, w, ok]) => (
            <Box key={t} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.25, borderBottom: `1px solid ${colors.border}`, '&:last-of-type': { borderBottom: 0 } }}>
              <Box sx={{ width: 34, height: 34, borderRadius: '50%', display: 'grid', placeItems: 'center', bgcolor: ok ? 'rgba(22,163,74,.12)' : colors.gray100, color: ok ? colors.success : colors.textTertiary, '& svg': { fontSize: 18 } }}>
                {ok ? <CheckRounded /> : <ScheduleRounded />}
              </Box>
              <Typography sx={{ flex: 1, fontWeight: 600, fontSize: 13.5 }}>{t}</Typography>
              <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>{w}</Typography>
            </Box>
          ))}
        </SectionCard>
      )}
    </>
  );
};

export default PaymentDetailPage;
